import React, { useState, useEffect, useRef } from 'react';
import { Form, Badge, OverlayTrigger, Tooltip, Popover, ListGroup, Button, Alert, Overlay } from 'react-bootstrap';

// Event to close all popovers when a new one opens
const CLOSE_POPOVERS_EVENT = 'closeAllPopovers';

const EditableField = ({ 
  field, 
  value, 
  originalValue, 
  onChange, 
  onValidationError,
  isEditable = false, 
  className = '',
  size = 'sm',
  modelValues = null // { model1: value, model2: value, model3: value }
}) => {
  const [localValue, setLocalValue] = useState(value != null ? String(value) : '');
  const [validationError, setValidationError] = useState('');
  const [isModified, setIsModified] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const inputRef = useRef(null);
  const popoverTarget = useRef(null);
  const popoverRef = useRef(null);
  const fieldId = useRef(`field-${Math.random().toString(36).substr(2, 9)}`);

  useEffect(() => {
    setLocalValue(value != null ? String(value) : '');
    setIsModified(isValueModified(value, originalValue));
  }, [value, originalValue]);

  // Listen for close popover events from other fields
  useEffect(() => {
    const handleClosePopovers = (event) => {
      // Close this popover if the event came from a different field
      if (event.detail.fieldId !== fieldId.current) {
        setShowPopover(false);
      }
    };

    window.addEventListener(CLOSE_POPOVERS_EVENT, handleClosePopovers);
    return () => {
      window.removeEventListener(CLOSE_POPOVERS_EVENT, handleClosePopovers);
    };
  }, []);

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPopover) {
        // Check if click is outside both the input and the popover
        const clickedInput = inputRef.current && inputRef.current.contains(event.target);
        const clickedPopover = popoverRef.current && popoverRef.current.contains(event.target);
        
        if (!clickedInput && !clickedPopover) {
          setShowPopover(false);
        }
      }
    };

    if (showPopover) {
      // Use timeout to ensure popover is rendered before adding listener
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPopover]);

  const isValueModified = (current, original) => {
    // Normalize values for comparison
    const normalizeValue = (val) => {
      if (val === null || val === undefined || val === '') return '';
      
      // IRIS-238: Compare string representations directly to preserve decimal precision.
      // Previously, Number("22.00") === Number("22") would mask real changes.
      return String(val).trim();
    };
    
    // Normalize date formats for comparison (YYYYMMDD vs YYYY-MM-DD)
    const normalizeDateFormat = (val) => {
      if (!val) return '';
      const str = String(val).trim();
      // Convert YYYY-MM-DD to YYYYMMDD for comparison
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        return str.replace(/-/g, '');
      }
      return str;
    };
    
    let normalizedCurrent = normalizeValue(current);
    let normalizedOriginal = normalizeValue(original);
    
    // If this is a date field, normalize date formats
    if (field?.dataType?.toLowerCase() === 'date') {
      normalizedCurrent = normalizeDateFormat(normalizedCurrent);
      normalizedOriginal = normalizeDateFormat(normalizedOriginal);
    }
    
    return normalizedCurrent !== normalizedOriginal;
  };

  const validateValue = (inputValue) => {
    if (!field) return '';
    
    // For date fields, validate even if empty or incomplete
    if (field.dataType?.toLowerCase() === 'date' && inputValue) {
      const dateRegexYMD = /^\d{4}-\d{2}-\d{2}$/;
      const dateRegexYYYYMMDD = /^\d{8}$/;
      const dateRegexDDMMYYYY = /^\d{2}-\d{2}-\d{4}$/;
      
      // Check if it matches expected formats
      if (dateRegexYMD.test(inputValue) || dateRegexYYYYMMDD.test(inputValue)) {
        // Validate actual date
        let testDate;
        if (dateRegexYYYYMMDD.test(inputValue)) {
          const year = inputValue.slice(0, 4);
          const month = inputValue.slice(4, 6);
          const day = inputValue.slice(6, 8);
          testDate = new Date(year, month - 1, day);
        } else {
          testDate = new Date(inputValue);
        }
        
        if (isNaN(testDate.getTime())) {
          return 'Please enter a valid date';
        }
      } else if (dateRegexDDMMYYYY.test(inputValue)) {
        // Handle dd-mm-yyyy format
        const parts = inputValue.split('-');
        const testDate = new Date(parts[2], parts[1] - 1, parts[0]);
        if (isNaN(testDate.getTime())) {
          return 'Please enter a valid date';
        }
      } else {
        // Try generic date parsing for other formats
        const testDate = new Date(inputValue);
        if (isNaN(testDate.getTime())) {
          return 'Please enter a valid date';
        }
      }
    }
    
    if (!inputValue) return '';

    switch (field.dataType?.toLowerCase()) {
      case 'number':
      case 'currency':
        if (isNaN(inputValue) || isNaN(parseFloat(inputValue))) {
          return 'Please enter a valid number';
        }
        break;
      case 'boolean':
        if (!['true', 'false', '1', '0', 'yes', 'no'].includes(inputValue.toLowerCase())) {
          return 'Please enter true/false, yes/no, or 1/0';
        }
        break;
    }
    
    return '';
  };

  const formatDateForStorage = (dateValue) => {
    if (!dateValue || field?.dataType?.toLowerCase() !== 'date') return dateValue;
    
    // Convert YYYY-MM-DD back to YYYYMMDD for storage
    const dateStr = String(dateValue).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr.replace(/-/g, '');
    }
    return dateValue;
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;
    const storageValue = formatDateForStorage(inputValue);
    
    setLocalValue(storageValue);
    
    const error = validateValue(storageValue);
    setValidationError(error);
    setIsModified(isValueModified(storageValue, originalValue));
    
    if (onChange) {
      onChange(storageValue, !!error);
    }
    
    if (onValidationError) {
      onValidationError(!!error, error);
    }
  };

  const handleFocus = () => {
    // Close all other popovers first
    window.dispatchEvent(new CustomEvent(CLOSE_POPOVERS_EVENT, { 
      detail: { fieldId: fieldId.current } 
    }));
    
    // Show popover when field is focused if model values are available
    if (isEditable && modelValues) {
      setShowPopover(true);
    }
  };

  const handleClick = () => {
    // Close all other popovers first
    window.dispatchEvent(new CustomEvent(CLOSE_POPOVERS_EVENT, { 
      detail: { fieldId: fieldId.current } 
    }));
    
    // Show popover when field is clicked if model values are available
    if (isEditable && modelValues) {
      setShowPopover(true);
    }
  };

  const handleSelectModelValue = (selectedValue) => {
    const storageValue = formatDateForStorage(selectedValue);
    
    setLocalValue(storageValue);
    
    const error = validateValue(storageValue);
    setValidationError(error);
    setIsModified(isValueModified(storageValue, originalValue));
    
    if (onChange) {
      onChange(storageValue, !!error);
    }
    
    if (onValidationError) {
      onValidationError(!!error, error);
    }
    
    // Close popover immediately after selection
    setShowPopover(false);
  };

  const hasModelValues = () => {
    if (!modelValues) return false;
    return modelValues.model1 !== undefined || 
           modelValues.model2 !== undefined || 
           modelValues.model3 !== undefined;
  };

  const getUniqueModelValues = () => {
    if (!modelValues) return [];
    
    const values = [];
    const seen = new Set();
    
    // Add model values in order, avoiding duplicates
    [
      { label: 'Model 1', value: modelValues.model1 },
      { label: 'Model 2', value: modelValues.model2 },
      { label: 'Model 3', value: modelValues.model3 }
    ].forEach(({ label, value }) => {
      if (value !== undefined && value !== null) {
        const normalizedValue = String(value).trim();
        if (normalizedValue && !seen.has(normalizedValue)) {
          seen.add(normalizedValue);
          values.push({ label, value: normalizedValue });
        }
      }
    });
    
    return values;
  };

  const handleBlur = (e) => {
    // For date inputs, check if the visual value is invalid even if e.target.value is empty
    if (field?.dataType?.toLowerCase() === 'date' && e.target.type === 'date') {
      // If the input shows invalid date text but value is empty, it means invalid input
      if (!e.target.value && e.target.validity && !e.target.validity.valid) {
        // Set error for invalid date format
        const error = 'Please enter a valid date';
        setValidationError(error);
        
        if (onValidationError) {
          onValidationError(true, error);
        }
        
        if (onChange) {
          onChange('', true);
        }
        return;
      }
    }
    
    // Always validate on blur, especially for incomplete values
    const error = validateValue(localValue);
    setValidationError(error);
    
    if (onValidationError) {
      onValidationError(!!error, error);
    }
    
    // Only call onChange if there's an error OR if the value actually changed
    if (error && onChange) {
      onChange(localValue, true);
    } else if (!error && onChange && localValue !== value) {
      onChange(localValue, false);
    }
  };

  const getInputType = () => {
    if (!field?.dataType) return 'text';
    
    switch (field.dataType.toLowerCase()) {
      case 'number':
      case 'currency':
        // Use text input to preserve trailing zeros (e.g., "2.00" not "2")
        // HTML number inputs strip trailing zeros from their value
        return 'text';
      case 'date':
        return 'date';
      default:
        return 'text';
    }
  };

  const formatDateForInput = (dateValue) => {
    if (!dateValue || field?.dataType?.toLowerCase() !== 'date') return dateValue;
    
    // Convert YYYYMMDD to YYYY-MM-DD for date inputs
    const dateStr = String(dateValue).trim();
    if (/^\d{8}$/.test(dateStr)) {
      return `${dateStr.slice(0,4)}-${dateStr.slice(4,6)}-${dateStr.slice(6,8)}`;
    }
    return dateValue;
  };

  const normalizeBooleanValue = (value) => {
    if (!value) return '';
    const val = String(value).toLowerCase();
    if (['true', '1', 'yes'].includes(val)) return 'true';
    if (['false', '0', 'no'].includes(val)) return 'false';
    return value;
  };

  const getInputProps = () => {
    const displayValue = formatDateForInput(localValue);
    
    const props = {
      type: getInputType(),
      value: displayValue,
      onChange: handleChange,
      onFocus: handleFocus,
      onClick: handleClick,
      onBlur: handleBlur,
      size,
      className: `${className} ${validationError ? 'is-invalid' : ''}`,
      style: isModified ? { backgroundColor: '#fff3cd' } : {},
      placeholder: '',
      ref: inputRef
    };

    if (field?.dataType?.toLowerCase() === 'number' || field?.dataType?.toLowerCase() === 'currency') {
      props.step = 'any';
    }

    return props;
  };

  const renderModelValuesPopover = (props) => {
    const uniqueValues = getUniqueModelValues();
    
    return (
      <Popover 
        id={`popover-${field?.fieldKey || 'field'}`} 
        style={{ maxWidth: '400px' }}
        ref={popoverRef}
        {...props}
      >
        <Popover.Header as="h3">Select AI Model Value</Popover.Header>
        <Popover.Body>
          {uniqueValues.length > 0 ? (
            <>
              <ListGroup>
                {uniqueValues.map((item, index) => (
                  <ListGroup.Item
                    key={index}
                    action
                    onClick={() => handleSelectModelValue(item.value)}
                    className="d-flex justify-content-between align-items-center"
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      <Badge bg="secondary" className="me-2">{item.label}</Badge>
                      <span>{item.value}</span>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
              <div className="mt-2 text-muted small">
                Or type your own value in the field above
              </div>
            </>
          ) : (
            <Alert variant="info" className="mb-2">
              <small>
                <strong>No values captured</strong><br />
                None of the AI models extracted a value for this field. Please enter the value manually by typing in the field above.
              </small>
            </Alert>
          )}
          <Button 
            variant="outline-secondary" 
            size="sm" 
            className="mt-2 w-100"
            onClick={(e) => {
              e.stopPropagation();
              setShowPopover(false);
            }}
          >
            Close
          </Button>
        </Popover.Body>
      </Popover>
    );
  };

  if (!isEditable) {
    return (
      <div className="d-flex align-items-center">
        <span className={localValue !== '' ? '' : 'text-muted fst-italic'}>
          {localValue !== '' ? localValue : 'Not extracted'}
        </span>
        {isModified && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Original value: {originalValue || 'None'}</Tooltip>}
          >
            <Badge bg="info" className="ms-2 small">
              Modified
            </Badge>
          </OverlayTrigger>
        )}
      </div>
    );
  }

  if (field?.dataType?.toLowerCase() === 'boolean') {
    return (
      <div ref={popoverTarget}>
        <Form.Select
          size={size}
          value={normalizeBooleanValue(localValue)}
          onChange={handleChange}
          onFocus={handleFocus}
          onClick={handleClick}
          onBlur={handleBlur}
          className={`${className} ${validationError ? 'is-invalid' : ''}`}
          style={isModified ? { backgroundColor: '#fff3cd' } : {}}
          ref={inputRef}
        >
          <option value="">Select...</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </Form.Select>
        
        {validationError && (
          <Form.Control.Feedback type="invalid" className="d-block">
            {validationError}
          </Form.Control.Feedback>
        )}
        
        {isEditable && modelValues && (
          <Overlay
            show={showPopover}
            target={inputRef.current}
            placement="bottom"
            containerPadding={20}
          >
            {renderModelValuesPopover()}
          </Overlay>
        )}
      </div>
    );
  }

  return (
    <div 
      ref={popoverTarget} 
      style={{ position: 'relative', cursor: isEditable && modelValues ? 'pointer' : 'default' }}
      onClick={handleClick}
    >
      <Form.Control {...getInputProps()} />
      
      {validationError && (
        <Form.Control.Feedback type="invalid" className="d-block">
          {validationError}
        </Form.Control.Feedback>
      )}
      
      {isEditable && modelValues && (
        <Overlay
          show={showPopover}
          target={inputRef.current}
          placement="bottom"
          containerPadding={20}
        >
          {renderModelValuesPopover()}
        </Overlay>
      )}
    </div>
  );
};

export default EditableField;